import type * as React from 'react';

import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BrandChip } from '@/components/shell';
import type { Role } from '@/constants/roles';

interface RoleChipsProps {
  roles: readonly Role[];
}

// Topbar user-meta role rendering. The canonical mockup (`#screen-users-list`
// .user-chip, mockups.html ~L1883) renders the role as a soft brand pill:
//   <span class="chip brand" style="font-size:10px; padding:1px 7px;">admin</span>
// i.e. `--mui-primary-soft` bg + `--mui-primary` text — the §6.4 `.chip.brand`.
// We reuse the shared `BrandChip` (variant="brand") so the topbar role pill and
// the admin-table role chips share one source of truth. A compact `fontSize`
// keeps it proportional to the user-chip name above it.
//
// Multiple roles (e.g. `test-multirole` → admin + backoffice + dispatcher)
// render as a wrapping row of brand pills.
const ROLE_CHIP_FONT_SIZE = 10;

export const RoleChips = ({ roles }: RoleChipsProps): React.JSX.Element => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roles.map((role) => (
        <BrandChip key={role} variant="brand" fontSize={ROLE_CHIP_FONT_SIZE}>
          {t(`role.${role}`)}
        </BrandChip>
      ))}
    </Stack>
  );
};
